import { applySafeArea, applyTheme, initLineTheme, applySysConfigurations, loadSysConfigurations } from '../../../../stores/common';
import { applyBusConfigsFromApp, applyCountryFromApp, initBusConfigsFromApi, initCountry } from '../../../../stores/bus-config';
import { applyApplySeedFromTheme } from '../../../../stores/apply-seed';
import { applyUserInfo, getUserInfo, userState } from '../../../../stores/user';
import { applyH5Lang } from '../../../../i18n';
import { applyRNUserToken, initRNBridge, postToApp, registerRNHandler, type RNMessage } from '../../../../lib/rnBridge';

export const OPEN_ACCOUNT_BOOTSTRAP_REQUEST_TYPE = 'WEB_TO_APP_OpenAccountBootstrap_Request';
export const OPEN_ACCOUNT_BOOTSTRAP_TYPE = 'APP_TO_WEB_OpenAccountBootstrap';
export const OPEN_ACCOUNT_TOKEN_EXPIRED_TYPE = 'WEB_TO_APP_OpenAccountTokenExpired';

export type OpenAccountTokenExpiredReason = 'http-401' | 'biz-401' | 'missing-session' | string;

type OpenAccountBootstrapPayload = {
  authorization?: string | null;
  userId?: string | number | null;
  applyStatus?: string | null;
  lang?: string | null;
  scheme?: 'light' | 'dark' | string | null;
  riseFallMode?: string | null;
  safeArea?: {
    top?: number;
    bottom?: number;
  } | null;
  configurations?: unknown;
  country?: unknown;
  busConfigs?: unknown;
  userInfo?: unknown;
  applySeeded?: boolean;
  applyType?: string | null;
  applyInfo?: unknown;
  applyList?: unknown;
};

type OpenAccountBootstrapMessage = RNMessage & {
  type: typeof OPEN_ACCOUNT_BOOTSTRAP_TYPE;
  requestId?: string;
  payload?: OpenAccountBootstrapPayload;
};

export type OpenAccountSession = {
  requestId: string;
  authorization: string | null;
  userId: string;
  applyStatus: string;
};

export class OpenAccountBootstrapTimeoutError extends Error {
  readonly code = 'OPEN_ACCOUNT_BOOTSTRAP_TIMEOUT';

  constructor() {
    super('Open account WebView bootstrap timeout');
    this.name = 'OpenAccountBootstrapTimeoutError';
  }
}

function isAppWebView(): boolean {
  return typeof window !== 'undefined' && Boolean((window as any).ReactNativeWebView);
}

export function isOpenAccountDocument(): boolean {
  if (typeof window === 'undefined') return false;
  return /\/(?:open-progress|add-account)(?:\/|$)/i.test(window.location.pathname);
}

export function isOpenAccountAppWebView(): boolean {
  return isOpenAccountDocument() && isAppWebView();
}

function normalizeText(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

function normalizeDict(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return Object.keys(value as object).length > 0 ? (value as Record<string, unknown>) : null;
}

async function applyBootstrap(message: OpenAccountBootstrapMessage): Promise<OpenAccountSession> {
  const payload = message.payload ?? {};
  const safeArea = payload.safeArea ?? {};
  const authorization = normalizeText(payload.authorization) || null;
  const userId = normalizeText(payload.userId);
  const applyStatus = normalizeText(payload.applyStatus);

  userState.isLogin = Boolean(authorization);
  userState.id = userId;
  userState.applyStatus = applyStatus;

  applyTheme(payload.scheme);
  if (payload.riseFallMode) initLineTheme(payload.riseFallMode);

  const configurations = normalizeDict(payload.configurations);
  console.log('[encryptKey][OpenAccountBootstrap]', {
    hasConfigurations: !!configurations,
    encryptKey: configurations?.encryptKey != null && configurations.encryptKey !== ''
      ? String(configurations.encryptKey)
      : '(none)',
  });
  if (configurations) applySysConfigurations(configurations);

  const country = normalizeDict(payload.country);
  if (country) applyCountryFromApp(country);

  const busConfigs = normalizeDict(payload.busConfigs);
  if (busConfigs) applyBusConfigsFromApp(busConfigs);

  const userInfo = normalizeDict(payload.userInfo);
  if (userInfo) applyUserInfo(userInfo);

  applyApplySeedFromTheme(payload);
  void applyH5Lang(payload.lang);

  applySafeArea({
    bottom: typeof safeArea.bottom === 'number' ? Math.max(0, safeArea.bottom) : 0,
    top: typeof safeArea.top === 'number' ? Math.max(0, safeArea.top) : 0,
    insets: {
      bottom: typeof safeArea.bottom === 'number' ? Math.max(0, safeArea.bottom) : 0,
      top: typeof safeArea.top === 'number' ? Math.max(0, safeArea.top) : 0,
      left: 0,
      right: 0
    }
  });

  const session = {
    requestId: normalizeText(message.requestId),
    authorization,
    userId,
    applyStatus
  };
  openAccountSession = session;

  if (authorization) {
    const fallbackTasks: Promise<unknown>[] = [];
    if (!configurations) {
      console.log('[encryptKey][OpenAccountBootstrap] fallback → loadSysConfigurations');
      fallbackTasks.push(loadSysConfigurations().catch(() => {}));
    }
    if (!country) fallbackTasks.push(initCountry().catch(() => {}));
    if (!busConfigs) fallbackTasks.push(initBusConfigsFromApi().catch(() => {}));
    if (!userInfo) fallbackTasks.push(getUserInfo().catch(() => {}));
    if (fallbackTasks.length > 0) await Promise.all(fallbackTasks);
    if (openAccountTokenExpiredNotified) throw new Error('OPEN_ACCOUNT_TOKEN_EXPIRED');
  }

  return session;
}

let openAccountBootstrapPromise: Promise<OpenAccountSession | null> | null = null;
let openAccountSession: OpenAccountSession | null = null;
let openAccountTokenExpiredNotified = false;

/**
 * App WebView 下主动请求开户快照；普通浏览器直接返回，让现有 H5 登录链路继续工作。
 */
export function waitForOpenAccountBootstrap(timeoutMs = 8000): Promise<OpenAccountSession | null> {
  if (!isOpenAccountAppWebView()) return Promise.resolve(null);
  if (openAccountBootstrapPromise) return openAccountBootstrapPromise;

  initRNBridge();
  const requestId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `open-account-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  openAccountBootstrapPromise = new Promise<OpenAccountSession>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let unsubscribe: (() => void) | undefined;

    const cleanup = () => {
      if (timer !== undefined) clearTimeout(timer);
      unsubscribe?.();
      unsubscribe = undefined;
    };

    const finish = (session: OpenAccountSession) => {
      if (settled) return;
      settled = true;
      cleanup();
      openAccountSession = session;
      resolve(session);
    };

    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      openAccountBootstrapPromise = null;
      reject(error);
    };

    unsubscribe = registerRNHandler(message => {
      if (message.type !== OPEN_ACCOUNT_BOOTSTRAP_TYPE) return;
      if ((message as OpenAccountBootstrapMessage).requestId !== requestId) return;
      void applyBootstrap(message as OpenAccountBootstrapMessage)
        .then(session => {
          if (!session.authorization) {
            notifyOpenAccountTokenExpired('missing-session');
            fail(new Error('OPEN_ACCOUNT_AUTH_REQUIRED'));
            return;
          }
          finish(session);
        })
        .catch(fail);
    });

    postToApp({
      type: OPEN_ACCOUNT_BOOTSTRAP_REQUEST_TYPE,
      requestId,
      timestamp: Date.now()
    });

    timer = setTimeout(() => fail(new OpenAccountBootstrapTimeoutError()), timeoutMs);
  });

  return openAccountBootstrapPromise;
}

export function getOpenAccountSession(): OpenAccountSession | null {
  return openAccountSession;
}

/** SZCA 整页回跳：URL 带 ca=1，内存会话已清空。 */
function isCaReturnDocument(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('ca') === '1';
  } catch {
    return false;
  }
}

/**
 * 大陆用户才会走 CA。进开户并确认大陆后，把当前开户 token 落到 localStorage，
 * 供整页回跳后、Bootstrap 到达前使用。非大陆不写。
 */
export function persistMainlandOpenAccountToken(): void {
  const token = openAccountSession?.authorization;
  if (!token) return;
  applyRNUserToken(token);
}

/**
 * 非开户页面返回 undefined；App 开户页在 Bootstrap 前返回 null，禁止 fallback 到浏览器旧 token。
 * 例外：CA 回跳（ca=1）且本次尚未判定 token 失效时，读进入开户时落下的 localStorage token。
 */
export function getOpenAccountAuthorization(): string | null | undefined {
  if (!isOpenAccountDocument()) return undefined;
  if (!isAppWebView()) return undefined;
  const sessionAuth = openAccountSession?.authorization ?? null;
  if (sessionAuth) return sessionAuth;
  if (openAccountTokenExpiredNotified) return null;
  if (isCaReturnDocument()) return localStorage.getItem('token');
  return null;
}

export function notifyOpenAccountTokenExpired(reason: OpenAccountTokenExpiredReason): void {
  if (!isOpenAccountAppWebView() || openAccountTokenExpiredNotified) return;
  openAccountTokenExpiredNotified = true;

  if (openAccountSession) {
    openAccountSession = {
      ...openAccountSession,
      authorization: null,
      userId: '',
      applyStatus: ''
    };
  }
  userState.isLogin = false;
  userState.id = '';
  userState.applyStatus = '';

  postToApp({
    type: OPEN_ACCOUNT_TOKEN_EXPIRED_TYPE,
    requestId: openAccountSession?.requestId,
    payload: { reason },
    timestamp: Date.now()
  });
}
